import {HttpClient} from '@angular/common/http';
import {APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';

import {FlexLayoutModule} from '@angular/flex-layout';
import {
  provideTranslateService,
  TranslateDirective,
  TranslateLoader,
  TranslatePipe,
  TranslateService,
  TranslationObject,
} from '@ngx-translate/core';

import {registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {PortalCommonsModule} from '@portal-commons/portal-commons.module';

import {environment} from '@environment';
import {MessageService} from '@portal-commons/message.service';
import {PortalAuthService} from '@portal-commons/portal-auth.service';
import {CustomErrorHandlerService} from '@portal-commons/providers/custom-error-handler.service';
import {LoggingService} from '@portal-commons/providers/logging.service';
import {NgcCookieConsentService} from 'ngx-cookieconsent';
import {NgxSmartModalModule} from 'ngx-smart-modal';
import {ToastrModule} from 'ngx-toastr';
import {catchError, Observable, timeout} from 'rxjs';
import {CookieConsentModule} from './misc/cookie-consent/cookie-consent.module';
import {SecondaryNavigationComponent} from './misc/secondary-navigation/secondary-navigation.component';

export class TranslateFallbackHttpLoader implements TranslateLoader {
  private static readonly WAIT_FOR_SERVER = 2000;

  constructor(
    private http: HttpClient,
    public prefix: string,
    public suffix: string,
    public fallbackPrefix: string = environment.i18nLocation,
    public fallbackSuffix: string = '.json',
  ) {}

  public getTranslation(lang: string): Observable<TranslationObject> {
    return this.http.get(`${this.prefix}${lang}${this.suffix}`).pipe(
      timeout(TranslateFallbackHttpLoader.WAIT_FOR_SERVER),
      catchError(error => {
        console.log('Translation: Failed to get translations from ' + this.prefix, error);
        console.log('Translation: Falling back to local files ' + this.fallbackPrefix);

        return this.http.get(`${this.fallbackPrefix}${lang}${this.fallbackSuffix}`);
      }),
    ) as Observable<TranslationObject>;
  }
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateFallbackHttpLoader(http, environment.endpoints.i18n, '.json');
}

registerLocaleData(localeDe);

export function initializeApp(auth: PortalAuthService, translate: TranslateService) {
  return () => {
    // the lang to use, if the lang isn't available, it will use the current loader to get them
    return translate.use('de');
  };
}

@NgModule({
  declarations: [AppComponent, SecondaryNavigationComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FlexLayoutModule,
    PortalCommonsModule.forRoot(),
    TranslatePipe,
    TranslateDirective,
    NgxSmartModalModule.forRoot(),
    CookieConsentModule,
    ToastrModule.forRoot({
      timeOut: 10000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }), // ToastrModule added,
  ],
  providers: [
    {provide: LOCALE_ID, useValue: 'de-DE'},
    MessageService,
    {provide: ErrorHandler, useClass: CustomErrorHandlerService, deps: [LoggingService, TranslateService, Injector]},
    NgcCookieConsentService,
    {provide: APP_INITIALIZER, useFactory: initializeApp, deps: [PortalAuthService, TranslateService], multi: true},
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
